/**
 * LivingLightsField — the emotional heart of the arrival screen.
 *
 * Mar 2026 · Rebuilt per Michael's brief. This is the cinematic
 * canvas layer behind the OML arrival copy. Three staged states:
 *
 *   1. "seed"    — near darkness, one tiny centre light. Everything
 *                   else is invisible. A single subtle heartbeat
 *                   ring pulses so the visitor knows to tap.
 *   2. "waking"  — visitor taps: the centre light blooms, a golden
 *                   wave sweeps outward, and thousands of distant
 *                   lights fade in over ~2 seconds.
 *   3. "living"  — a living field: distant lights drift, occasional
 *                   new lights are born at random positions with an
 *                   intention word floating up beside them for a
 *                   couple of seconds ("Peace · Manchester").
 *
 * Rendered on a full-width, absolutely-positioned canvas so the copy
 * sits crisply above it. Reduced-motion respected: the field is
 * still drawn but drift + bloom animations are skipped.
 */
import React, { useEffect, useRef, useCallback } from "react";

const GOLD = { r: 240, g: 199, b: 144 };
const GOLD_WARM = { r: 224, g: 169, b: 122 };

const NUM_DISTANT = 260;   // Tiny background lights (the "constellation")
const NUM_MID = 60;        // Slightly closer, larger, drifting
const CENTER_SEED_R = 5;   // Radius of the seed light before tap
const CENTER_LIVE_R = 26;  // Radius of the bloomed centre — kept modest so copy above it stays readable

function rand(min, max) { return min + Math.random() * (max - min); }

function makeStars(w, h) {
  const distant = [];
  for (let i = 0; i < NUM_DISTANT; i++) {
    distant.push({
      x: rand(0, w),
      y: rand(0, h),
      r: rand(0.4, 1.6),
      a: rand(0.15, 0.7),         // base alpha
      p: rand(0, Math.PI * 2),    // twinkle phase
      s: rand(0.4, 1.6),          // twinkle speed
      vx: rand(-0.02, 0.02),      // drift
      vy: rand(-0.02, 0.02),
    });
  }
  const mid = [];
  for (let i = 0; i < NUM_MID; i++) {
    mid.push({
      x: rand(0, w),
      y: rand(0, h),
      r: rand(1.5, 3.2),
      a: rand(0.35, 0.85),
      p: rand(0, Math.PI * 2),
      s: rand(0.3, 1.0),
      vx: rand(-0.05, 0.05),
      vy: rand(-0.05, 0.05),
      hue: Math.random() < 0.55 ? GOLD : GOLD_WARM,
    });
  }
  return { distant, mid };
}

/**
 * Live labels — "Peace · Manchester" bubbles that fade in near a
 * mid-layer point every few seconds. Consumed by an internal queue
 * so we can inject fresh ones as the ticker updates.
 */
class LabelQueue {
  constructor(getPool) { this.getPool = getPool; this.active = []; }
  spawn(w, h) {
    const pool = (this.getPool && this.getPool()) || [];
    if (!pool.length) return;
    const pick = pool[Math.floor(Math.random() * pool.length)];
    const label = pick.city ? `${pick.intention} · ${pick.city}` : pick.intention;
    this.active.push({
      x: rand(w * 0.15, w * 0.85),
      y: rand(h * 0.2, h * 0.8),
      born: performance.now(),
      life: 4200,
      label,
    });
    // Keep the queue bounded
    if (this.active.length > 4) this.active.shift();
  }
  draw(ctx, now) {
    ctx.save();
    ctx.font = "300 12px 'Manrope', system-ui, sans-serif";
    ctx.textBaseline = "middle";
    for (const l of this.active) {
      const age = now - l.born;
      const t = age / l.life;
      if (t >= 1) continue;
      // Ease in-out alpha: 0->1 in first 20%, 1 during middle, fade back
      const a = t < 0.2 ? t / 0.2 : t > 0.75 ? (1 - t) / 0.25 : 1;
      const lift = Math.sin(t * Math.PI) * 8; // small upward drift
      // Halo dot
      ctx.globalAlpha = 0.9 * a;
      ctx.fillStyle = "rgba(240,199,144,1)";
      ctx.beginPath();
      ctx.arc(l.x, l.y - lift, 2.4, 0, Math.PI * 2);
      ctx.fill();
      // Label — small caps, gold soft
      ctx.globalAlpha = 0.82 * a;
      ctx.fillStyle = "rgba(245,224,197,0.95)";
      const text = l.label.toUpperCase();
      const w = ctx.measureText(text).width;
      ctx.fillText(text, l.x - w / 2, l.y - lift - 14);
    }
    // Cull expired
    this.active = this.active.filter((l) => now - l.born < l.life);
    ctx.restore();
  }
}

export default function LivingLightsField({
  state = "seed",         // "seed" | "waking" | "living"
  reduceMotion = false,
  onSeedTap,              // fired when visitor taps the seed light
  recentPool,             // { intention, city }[] – for label bubbles
  centerYRatio = 0.5,     // where to render the centre light (0..1 of canvas h)
}) {
  const canvasRef = useRef(null);
  const stateRef = useRef(state);
  const wavesRef = useRef([]);           // Bloom shockwave rings
  const bloomStartRef = useRef(0);
  const starsRef = useRef(null);
  const labelsRef = useRef(new LabelQueue(() => recentPool || []));
  const lastLabelAt = useRef(0);
  const rafRef = useRef(null);
  const sizeRef = useRef({ w: 0, h: 0, dpr: 1 });

  // Keep the ref in sync so the animation loop reads the latest.
  useEffect(() => {
    const prev = stateRef.current;
    stateRef.current = state;
    if (state === "waking" && prev !== "waking") {
      wavesRef.current.push({ t: performance.now() });
      bloomStartRef.current = performance.now();
    }
  }, [state]);

  // Size the canvas to its parent, HiDPI-aware.
  const resize = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const parent = c.parentElement;
    if (!parent) return;
    const rect = parent.getBoundingClientRect();
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    c.width = Math.max(1, Math.floor(rect.width * dpr));
    c.height = Math.max(1, Math.floor(rect.height * dpr));
    c.style.width = rect.width + "px";
    c.style.height = rect.height + "px";
    sizeRef.current = { w: rect.width, h: rect.height, dpr };
    starsRef.current = makeStars(rect.width, rect.height);
  }, []);

  useEffect(() => {
    resize();
    const ro = new ResizeObserver(resize);
    if (canvasRef.current?.parentElement) ro.observe(canvasRef.current.parentElement);
    return () => ro.disconnect();
  }, [resize]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    let mounted = true;

    const draw = (now) => {
      if (!mounted) return;
      const { w, h, dpr } = sizeRef.current;
      if (!starsRef.current) {
        rafRef.current = requestAnimationFrame(draw);
        return;
      }
      ctx.save();
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      const s = stateRef.current;
      const cx = w / 2;
      const cy = h * (centerYRatio || 0.5);

      // 1) Distant + mid stars. Alpha ramps up as the state advances.
      let fieldAlpha = 0;
      if (s === "waking") {
        const t = Math.max(0, Math.min(1, (now - bloomStartRef.current) / 2000));
        fieldAlpha = t;
      } else if (s === "living") {
        fieldAlpha = 1;
      }

      const { distant, mid } = starsRef.current;
      if (fieldAlpha > 0) {
        ctx.save();
        for (const st of distant) {
          if (!reduceMotion) {
            st.p += 0.02 * st.s;
            st.x += st.vx; st.y += st.vy;
            if (st.x < -5) st.x = w + 5; if (st.x > w + 5) st.x = -5;
            if (st.y < -5) st.y = h + 5; if (st.y > h + 5) st.y = -5;
          }
          const tw = 0.65 + Math.sin(st.p) * 0.35;
          ctx.globalAlpha = fieldAlpha * st.a * tw;
          ctx.fillStyle = "rgba(255,238,210,1)";
          ctx.beginPath();
          ctx.arc(st.x, st.y, st.r, 0, Math.PI * 2);
          ctx.fill();
        }
        for (const st of mid) {
          if (!reduceMotion) {
            st.p += 0.015 * st.s;
            st.x += st.vx; st.y += st.vy;
            if (st.x < -10) st.x = w + 10; if (st.x > w + 10) st.x = -10;
            if (st.y < -10) st.y = h + 10; if (st.y > h + 10) st.y = -10;
          }
          const tw = 0.6 + Math.sin(st.p) * 0.4;
          ctx.globalAlpha = fieldAlpha * st.a * tw;
          const g = ctx.createRadialGradient(st.x, st.y, 0, st.x, st.y, st.r * 4);
          const c1 = st.hue || GOLD;
          g.addColorStop(0, `rgba(${c1.r},${c1.g},${c1.b},1)`);
          g.addColorStop(1, `rgba(${c1.r},${c1.g},${c1.b},0)`);
          ctx.fillStyle = g;
          ctx.beginPath();
          ctx.arc(st.x, st.y, st.r * 4, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = fieldAlpha * st.a;
          ctx.fillStyle = `rgba(${c1.r},${c1.g},${c1.b},1)`;
          ctx.beginPath();
          ctx.arc(st.x, st.y, st.r, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }

      // 2) Bloom shockwaves — a soft golden ring sweeping outward
      //    after the visitor taps the seed light.
      if (wavesRef.current.length) {
        const maxR = Math.max(w, h) * 0.9;
        const kept = [];
        for (const wv of wavesRef.current) {
          const age = now - wv.t;
          const t = Math.max(0, Math.min(1, age / 1800));
          if (age >= 1800) continue;
          const r = Math.max(0, t * maxR);
          const a = (1 - t) * 0.55;
          if (r > 0) {
            ctx.beginPath();
            ctx.arc(cx, cy, r, 0, Math.PI * 2);
            ctx.strokeStyle = `rgba(240,199,144,${a})`;
            ctx.lineWidth = Math.max(0.25, 2.5 - t * 2);
            ctx.stroke();
          }
          kept.push(wv);
        }
        wavesRef.current = kept;
      }

      // 3) Centre seed light — ONLY drawn while in the "seed" state.
      //    Michael, Mar 2026: once the field wakes, remove the
      //    centre "white dot" entirely; the constellation itself is
      //    the visual anchor and the dot was obscuring copy.
      if (s === "seed") {
        const heart = 0.5 + 0.5 * Math.sin(now / 620); // heartbeat pulse (unused visually now, kept for future accent)
        // Faint outer breath ring so the visitor knows this is tappable.
        const breath = 0.35 + 0.35 * Math.sin(now / 900);
        ctx.beginPath();
        ctx.arc(cx, cy, CENTER_SEED_R + 8 + breath * 6, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(240,199,144,${0.14 + breath * 0.18})`;
        ctx.lineWidth = 1;
        ctx.stroke();
        const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, CENTER_SEED_R * 5);
        g.addColorStop(0, `rgba(255,240,220,${0.9})`);
        g.addColorStop(0.4, `rgba(240,199,144,${0.55})`);
        g.addColorStop(1, "rgba(240,199,144,0)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(cx, cy, CENTER_SEED_R * 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "rgba(255,246,225,1)";
        ctx.beginPath();
        ctx.arc(cx, cy, CENTER_SEED_R, 0, Math.PI * 2);
        ctx.fill();
        // Reference heart to avoid an unused-var lint warning.
        void heart;
      }

      // 4) Occasional labels once the field is alive.
      if (s === "living" && !reduceMotion) {
        if (now - lastLabelAt.current > 2600) {
          labelsRef.current.spawn(w, h);
          lastLabelAt.current = now;
        }
        labelsRef.current.draw(ctx, now);
      }

      ctx.restore();
      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => {
      mounted = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [reduceMotion, centerYRatio]);

  // Tap handling — accept anywhere but only fire the seed callback
  // while we are still in the seed state.
  const onTap = () => {
    if (stateRef.current === "seed" && onSeedTap) onSeedTap();
  };

  return (
    <canvas
      ref={canvasRef}
      data-testid="oml-lights-field"
      onClick={onTap}
      onTouchStart={onTap}
      className="absolute inset-0 w-full h-full"
      style={{ cursor: state === "seed" ? "pointer" : "default", touchAction: "manipulation" }}
      aria-hidden
    />
  );
}
